import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import NumberFormat from "react-number-format";
import ReactPaginate from "react-paginate";
import { useDispatch } from "react-redux";
import swal from "@sweetalert/with-react";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import Table, { Td, Th } from "../../components/Table";
import Button from "../../components/Button";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";
import BigLoader from "../../components/BigLoader";

const STATUS_LABEL = {
  pending: "Menunggu",
  ready: "Siap Dibayar",
  paid: "Sudah Dibayar",
};

const STATUS_COLOR = {
  pending: "text-yellow-300",
  ready: "text-primary-500",
  paid: "text-green-300",
};

export default function Affiliate() {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [page, setPage] = useState(1);
  const [config, setConfig] = useState({ commissionRate: 20, refundPeriodDays: 7 });
  const [configLoading, setConfigLoading] = useState(false);

  const getData = () => {
    setLoading(true);
    service
      .get("/api/affiliate/admin/conversions", { params: { page } })
      .then((response) => {
        setTotal(response.data.total);
        setData(response.data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getConfig = () => {
    service
      .get("/api/affiliate/admin/config")
      .then((res) => setConfig(res.data))
      .catch(() => {});
  };

  const saveConfig = () => {
    setConfigLoading(true);
    service
      .put("/api/affiliate/admin/config", config)
      .then((res) => {
        setConfigLoading(false);
        setConfig(res.data);
        swal("Berhasil", "Pengaturan affiliate disimpan", "success");
      })
      .catch(() => {
        setConfigLoading(false);
        swal("Gagal", "Gagal menyimpan pengaturan", "error");
      });
  };

  const setStatus = (id, status, message) => {
    swal({
      title: "Ubah status?",
      text: message,
      icon: "warning",
      buttons: ["Batal", "Ya, lanjutkan"],
    }).then((ok) => {
      if (!ok) return;
      setLoadingPost(true);
      service
        .put(`/api/affiliate/admin/conversions/${id}/status`, { status })
        .then(() => {
          setLoadingPost(false);
          getData();
        })
        .catch((e) => {
          setLoadingPost(false);
          swal("Gagal", e?.response?.data?.error || "Gagal mengubah status", "error");
        });
    });
  };

  useEffect(() => {
    dispatch(setInterface({ active: "Affiliate", pageTitle: "Affiliate" }));
  }, [dispatch]);

  useEffect(() => {
    getData();
    getConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <Fragment>
      <Helmet>
        <title>Affiliate</title>
      </Helmet>

      <Card className="mb-5">
        <h2 className="font-bold text-lg mb-4">Pengaturan Komisi</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-black-300 mb-1">
              Komisi Global (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              className="h-11 px-3 rounded-sm border border-white-300 text-sm"
              value={config.commissionRate}
              onChange={(e) =>
                setConfig({ ...config, commissionRate: Number(e.target.value) })
              }
            />
          </div>
          <div>
            <label className="block text-xs text-black-300 mb-1">
              Masa Refund (hari)
            </label>
            <input
              type="number"
              min="0"
              className="h-11 px-3 rounded-sm border border-white-300 text-sm"
              value={config.refundPeriodDays}
              onChange={(e) =>
                setConfig({ ...config, refundPeriodDays: Number(e.target.value) })
              }
            />
          </div>
          <Button color="green" onClick={saveConfig} disabled={configLoading}>
            {configLoading ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="h-64 flex justify-center items-center flex-col">
            <Loader className="bg-black-100" />
            <span className="mt-3 text-black-300 text-sm">Memuat</span>
          </div>
        ) : data.length ? (
          <Fragment>
            <Table>
              <thead>
                <tr>
                  <Th>TANGGAL</Th>
                  <Th>AFFILIATE</Th>
                  <Th>KAMPANYE</Th>
                  <Th>PEMBELI</Th>
                  <Th>PENJUALAN</Th>
                  <Th>KOMISI</Th>
                  <Th>STATUS</Th>
                  <Th>AKSI</Th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={`list-${index}`}>
                    <Td className="text-center">
                      {moment(item.created_at).format("HH:mm DD/MM/YYYY")}
                    </Td>
                    <Td>
                      <div>{item.affiliate_name}</div>
                      <div className="text-xs text-black-300">{item.affiliate_code}</div>
                    </Td>
                    <Td>{item.campaign_name || "-"}</Td>
                    <Td>{item.buyer_name || "-"}</Td>
                    <Td className="text-right">
                      <NumberFormat
                        prefix="Rp"
                        thousandSeparator={true}
                        value={item.amount}
                        displayType="text"
                      />
                    </Td>
                    <Td className="text-right">
                      <NumberFormat
                        prefix="Rp"
                        thousandSeparator={true}
                        value={item.commission}
                        displayType="text"
                      />
                    </Td>
                    <Td className="text-center">
                      <span className={STATUS_COLOR[item.status]}>
                        {STATUS_LABEL[item.status] || item.status}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex justify-center gap-2">
                        {item.status === "pending" && (
                          <Button
                            color="yellow"
                            noFixedHeight
                            className="h-8 px-2 text-xs"
                            onClick={() =>
                              setStatus(
                                item.id,
                                "ready",
                                "Komisi akan berubah menjadi Siap Dibayar."
                              )
                            }
                          >
                            Siap Dibayar
                          </Button>
                        )}
                        {item.status === "ready" && (
                          <Button
                            color="green"
                            noFixedHeight
                            className="h-8 px-2 text-xs"
                            onClick={() =>
                              setStatus(
                                item.id,
                                "paid",
                                "Tandai komisi sudah dibayarkan ke affiliate."
                              )
                            }
                          >
                            Tandai Dibayar
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <ReactPaginate
              pageCount={total}
              pageRangeDisplayed={2}
              marginPagesDisplayed={2}
              initialPage={page - 1}
              containerClassName="flex flex-wrap mt-6 gap-1"
              previousLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              nextLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              pageLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              activeLinkClassName="bg-primary-500 border-primary-600 text-white-100"
              onPageChange={({ selected }) => setPage(selected + 1)}
            />
          </Fragment>
        ) : (
          <div className="text-center flex flex-col justify-center items-center h-40 text-black-300">
            <p>Belum ada penjualan affiliate</p>
          </div>
        )}
      </Card>

      {loadingPost && <BigLoader />}
    </Fragment>
  );
}
